
import { showToast } from '/src/v4/toast.js';
import { statTile, statusBadge, customerCell, activityItem, visitorRow, emptyState, escapeHtml } from '/src/v4/markup.js';

// Build a code-source row under each [data-source] card.
function indent(html) {
  // Lightly format the HTML for display: collapse whitespace + indent.
  // (Production HTML is already nicely indented in the source — but we
  // capture from the live DOM, so re-format for readability.)
  return html
    .replace(/></g, '>\n<')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
}

document.querySelectorAll('[data-source]').forEach((card) => {
  const preview = card.querySelector('[data-preview]');
  if (!preview) return;

  const originalHtml = preview.innerHTML.trim();
  const label = card.dataset.sourceLabel || '';
  const code = indent(originalHtml);
  const block = document.createElement('div');
  block.className = 'pg-source';
  block.innerHTML = `
    <div class="pg-source-head">
      <span class="pg-source-label">${label || 'HTML'} <span class="pg-edit-hint">— edit to preview</span></span>
      <div style="display:flex;gap:6px">
        <button type="button" class="btn btn-outline btn-sm pg-reset" hidden>↺ Reset</button>
        <button type="button" class="btn btn-outline btn-sm pg-copy" aria-label="Copy code">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="6" y="6" width="8" height="8" rx="1.5"/><path d="M3 10V3h7"/></svg>
          Copy
        </button>
      </div>
    </div>
    <pre class="pg-code pg-code-edit" contenteditable="plaintext-only" spellcheck="false" aria-label="HTML source — edit to update preview"></pre>
  `;
  const codeEl = block.querySelector('.pg-code');
  const resetBtn = block.querySelector('.pg-reset');
  codeEl.textContent = code;
  card.appendChild(block);

  // Live edit: debounce 250ms, set preview.innerHTML from textContent.
  // Browser auto-corrects malformed HTML — we don't try to validate.
  let editTimer;
  codeEl.addEventListener('input', () => {
    clearTimeout(editTimer);
    editTimer = setTimeout(() => {
      preview.innerHTML = codeEl.textContent;
      resetBtn.hidden = false;
    }, 250);
  });

  resetBtn.addEventListener('click', () => {
    preview.innerHTML = originalHtml;
    codeEl.textContent = indent(originalHtml);
    resetBtn.hidden = true;
  });

  block.querySelector('.pg-copy').addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(codeEl.textContent);
      showToast('Copied to clipboard', { variant: 'success' });
    } catch (_e) {
      showToast('Copy failed — select and Ctrl+C', { variant: 'error' });
    }
  });
});

// Scrollspy for the left rail.
const links = [...document.querySelectorAll('.pg-nav-link')];
const sections = [...document.querySelectorAll('.pg-block')];
const observer = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const id = entry.target.id;
      links.forEach((l) => l.classList.toggle('active', l.getAttribute('href') === '#' + id));
    }
  });
}, { rootMargin: '-30% 0px -60% 0px', threshold: 0 });
sections.forEach((s) => observer.observe(s));

// Smooth scroll
links.forEach((a) => a.addEventListener('click', (e) => {
  e.preventDefault();
  const t = document.querySelector(a.getAttribute('href'));
  if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
}));

// ── Async list lifecycle simulator ───────────────────────────────────
// Demonstrates the four states a real list goes through: loading → loaded /
// empty / error. Buttons let viewers trigger each transition.
const lifecycleHost = document.getElementById('async-list-host');
if (lifecycleHost) {
  const SAMPLE = [
    { name: 'Sarah K.',      plan: 'Pro',      status: 'Active',  mrr: '$199' },
    { name: 'Michael R.',    plan: 'Business', status: 'Active',  mrr: '$499' },
    { name: 'Emily W.',      plan: 'Starter',  status: 'Pending', mrr: '$49'  },
    { name: 'Diego R.',      plan: 'Pro',      status: 'Active',  mrr: '$199' }
  ];
  const renderIdle = () => {
    lifecycleHost.innerHTML = `<div style="text-align:center;color:var(--text-muted);font-size:13px;padding:60px 0">Click a button above to simulate a state.</div>`;
  };
  const renderLoading = () => {
    const row = `<tr>${'<td><span class="skeleton skeleton-text" style="width:70%"></span></td>'.repeat(4)}</tr>`;
    lifecycleHost.innerHTML = `
      <table class="table" style="width:100%">
        <thead><tr><th>Customer</th><th>Plan</th><th>Status</th><th style="text-align:right">MRR</th></tr></thead>
        <tbody>${row.repeat(4)}</tbody>
      </table>`;
  };
  const renderLoaded = () => {
    lifecycleHost.innerHTML = `
      <table class="table" style="width:100%">
        <thead><tr><th>Customer</th><th>Plan</th><th>Status</th><th style="text-align:right">MRR</th></tr></thead>
        <tbody>${SAMPLE.map((r) => `
          <tr>
            <td class="cell-strong">${r.name}</td>
            <td><span class="chip">${r.plan}</span></td>
            <td><span class="status status-${r.status === 'Active' ? 'green' : 'yellow'}">${r.status}</span></td>
            <td class="cell-strong" style="text-align:right">${r.mrr}</td>
          </tr>`).join('')}
        </tbody>
      </table>`;
  };
  const renderEmpty = () => {
    lifecycleHost.innerHTML = `
      <div class="empty-state" style="padding:32px 24px">
        <div class="empty-state-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="7"/><path d="M16 16l4 4"/></svg>
        </div>
        <div class="empty-state-title">No customers found</div>
        <div class="empty-state-text">Try changing your filters, or create your first customer to get started.</div>
        <div class="empty-state-actions">
          <button class="btn btn-primary btn-sm">+ New customer</button>
        </div>
      </div>`;
  };
  const renderError = (retry) => {
    lifecycleHost.innerHTML = `
      <div class="banner banner-danger">
        <svg class="banner-icon" width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="8" cy="8" r="6"/><path d="M5 5l6 6M11 5l-6 6"/></svg>
        <div class="banner-body"><strong>Couldn't load customers.</strong> Check your network connection and try again.</div>
        <div class="banner-actions"><button class="btn btn-outline btn-sm" id="lifecycle-retry">Retry</button></div>
      </div>`;
    document.getElementById('lifecycle-retry')?.addEventListener('click', retry);
  };

  renderIdle();

  document.querySelectorAll('[data-lifecycle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const state = btn.dataset.lifecycle;
      if (state === 'reset') { renderIdle(); return; }
      renderLoading();
      // Real fetch latency simulation. Devs reading this should note the
      // pattern: render skeleton synchronously, then resolve the data, then
      // render the final state. Never go from idle straight to loaded.
      setTimeout(() => {
        if (state === 'success') renderLoaded();
        else if (state === 'empty') renderEmpty();
        else if (state === 'error') {
          renderError(() => {
            renderLoading();
            setTimeout(renderLoaded, 700); // retry "succeeds"
          });
        }
      }, 700);
    });
  });
}

// ── Submit-spinner pattern ───────────────────────────────────────────
// Disable the button and swap the label for a spinner during the request,
// then restore on resolve. Keeps the user from double-submitting and gives
// clear "something is happening" feedback.
const submitForm = document.getElementById('submit-spinner-form');
if (submitForm) {
  submitForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = submitForm.querySelector('[data-pg-submit]');
    const spinner = btn.querySelector('.btn-spinner');
    const label = btn.querySelector('[data-pg-submit-label]');
    const original = label.textContent;
    btn.disabled = true;
    spinner.hidden = false;
    label.textContent = 'Saving…';
    try {
      // Simulate a 1.5s API call. Replace with await fetch(...)
      await new Promise((r) => setTimeout(r, 1500));
      showToast('Saved ✓', { variant: 'success' });
      submitForm.reset();
    } catch (err) {
      showToast('Failed to save', { variant: 'error' });
    } finally {
      btn.disabled = false;
      spinner.hidden = true;
      label.textContent = original;
    }
  });
}

// ── Markup helpers — populate the live previews ──────────────────────
const ICON_REVENUE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>';
const ICON_CLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';

const statHost = document.getElementById('helpers-stat-host');
if (statHost) {
  statHost.innerHTML = [
    statTile({ label: 'Revenue', value: '$84,520', color: 'green', iconHtml: ICON_REVENUE,
               change: { pct: '+18%', direction: 'up' }, subtext: '$3,218 today' }),
    statTile({ label: 'Pending', value: '42', color: 'yellow', iconHtml: ICON_CLOCK,
               change: { pct: '-3%', direction: 'down' }, subtext: '5 awaiting payment' })
  ].join('');
}

const statusHost = document.getElementById('helpers-status-host');
if (statusHost) {
  statusHost.innerHTML = [
    statusBadge('Paid', 'green'),
    statusBadge('Processing', 'blue'),
    statusBadge('Pending', 'yellow'),
    statusBadge('Failed', 'red'),
    statusBadge('Archived', 'gray')
  ].join('');
}

const customerHost = document.getElementById('helpers-customer-host');
if (customerHost) {
  const orders = [
    { name: 'Sarah Klein',   color: 'var(--primary)' },
    { name: 'Michael Reese', color: 'var(--blue)' },
    { name: 'Emily Wang',    color: 'var(--yellow)' }
  ];
  customerHost.innerHTML = orders.map((o) =>
    `<tr><td>${customerCell({ name: o.name, avatarColor: o.color })}</td></tr>`
  ).join('');
}

const activityHost = document.getElementById('helpers-activity-host');
if (activityHost) {
  const items = [
    { user: 'Sarah K.',  text: 'placed a new order for $245.00', initials: 'SK', bg: 'linear-gradient(135deg,var(--primary),var(--primary-dk))', time: '2 min ago' },
    { user: 'Michael R.', text: 'registered a new account',       initials: 'MR', bg: 'linear-gradient(135deg,var(--blue),#0550a0)',           time: '18 min ago' },
    { user: 'Payment',    text: 'processed — Invoice #4521',      initials: 'PA', bg: 'linear-gradient(135deg,var(--green),#1a8a32)',          time: '45 min ago' }
  ];
  activityHost.innerHTML = items.map((i) => activityItem({
    initials: i.initials, avatarBg: i.bg,
    bodyHtml: `<strong>${escapeHtml(i.user)}</strong> ${escapeHtml(i.text)}`,
    time: i.time
  })).join('');
}

const visitorHost = document.getElementById('helpers-visitor-host');
if (visitorHost) {
  const data = [
    { flag: '🇺🇸', country: 'United States', pct: 33 },
    { flag: '🇫🇷', country: 'France',        pct: 27 },
    { flag: '🇩🇪', country: 'Germany',       pct: 16 },
    { flag: '🇪🇸', country: 'Spain',         pct: 11 }
  ];
  visitorHost.innerHTML = data.map((d) =>
    visitorRow({ name: d.country, pct: d.pct, flag: d.flag })
  ).join('');
}

const emptyHost = document.getElementById('helpers-empty-host');
if (emptyHost) {
  emptyHost.innerHTML = emptyState({
    title: 'No orders yet',
    desc: "Orders will appear here once your first customer checks out.",
    iconHtml: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>',
    actionHtml: '<button class="btn btn-primary btn-sm">Create test order</button>'
  });
}
